import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart, Line,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import axios from "../../utils/axiosInstance";

function formatCad(n) {
  const x = Number(n || 0);
  if (!Number.isFinite(x)) return "0.00 CAD";
  return `${x.toFixed(2)} CAD`;
}

function StatCard({ title, value, tone = "blue" }) {
  const toneCls =
    tone === "green" ? "text-emerald-600 dark:text-emerald-300" :
    tone === "yellow" ? "text-amber-600 dark:text-amber-300" :
    tone === "purple" ? "text-violet-600 dark:text-violet-300" :
    "text-blue-600 dark:text-blue-300";

  return (
    <div className="p-4 rounded-2xl shadow-sm border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900">
      <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{title}</p>
      <p className={`mt-2 text-2xl font-semibold ${toneCls}`}>{value}</p>
    </div>
  );
}

export default function StatsPage() {
  const [range, setRange] = useState("30d"); // 7d | 30d | 90d
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const { from, to } = useMemo(() => {
    const end = new Date();
    const start = new Date(end);
    const days =
      range === "7d" ? 7 :
      range === "90d" ? 90 :
      30;
    start.setDate(end.getDate() - days);
    return {
      from: start.toISOString().slice(0, 10),
      to: end.toISOString().slice(0, 10),
    };
  }, [range]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setErr("");
      try {
        const res = await axios.get(`/api/admin/stats?from=${from}&to=${to}`);
        if (!mounted) return;
        setData(res.data);
      } catch (e) {
        console.error(e);
        setErr(e?.response?.data?.message || "Impossible de charger les statistiques.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => { mounted = false; };
  }, [from, to]);

  const kpis = data?.kpis || {};
  const walletSeries = data?.wallet?.seriesDailyCad || [];

  // Derive some extra KPIs from totals
  const walletTotals = useMemo(() => {
    const rows = data?.wallet?.txTotals || [];
    const transfers = data?.wallet?.transferTotals || [];

    let fundCad = 0, withdrawCad = 0, loanCad = 0;
    let fundCount = 0, withdrawCount = 0, loanCount = 0;
    let completed = 0, pending = 0, failed = 0;

    for (const r of rows) {
      const cad = Number(r.sumCad || 0);
      if (r.type === "fund") { fundCad += cad; fundCount += Number(r.count || 0); }
      if (r.type === "withdraw") { withdrawCad += cad; withdrawCount += Number(r.count || 0); }
      if (r.type === "loan") { loanCad += cad; loanCount += Number(r.count || 0); }

      if (r.status === "completed") completed += Number(r.count || 0);
      if (r.status === "pending") pending += Number(r.count || 0);
      if (r.status === "failed") failed += Number(r.count || 0);
    }

    // transfers totals (treat as "transfer" volume)
    let transferCad = 0;
    let transferCount = 0;
    for (const r of transfers) {
      transferCad += Number(r.sumCad || 0);
      transferCount += Number(r.count || 0);
      if (r.status === "completed") completed += Number(r.count || 0);
      if (r.status === "pending") pending += Number(r.count || 0);
      if (r.status === "failed") failed += Number(r.count || 0);
    }

    const totalOps = completed + pending + failed;
    const successRate = totalOps > 0 ? (completed / totalOps) * 100 : 0;

    return {
      fundCad, withdrawCad, loanCad, transferCad,
      fundCount, withdrawCount, loanCount, transferCount,
      totalOps, completed, pending, failed,
      successRate,
    };
  }, [data]);

  const barData = useMemo(() => ([
    { name: "Fund", cad: walletTotals.fundCad },
    { name: "Withdraw", cad: walletTotals.withdrawCad },
    { name: "Transfer", cad: walletTotals.transferCad },
    { name: "Loan", cad: walletTotals.loanCad },
  ]), [walletTotals]);

  return (
    <div className="space-y-6 pt-6 md:ml-64 p-4">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-white">Statistiques</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Données réelles (wallet + transferts + commissions) • Devise ledger: CAD
          </p>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white"
          >
            <option value="7d">7 jours</option>
            <option value="30d">30 jours</option>
            <option value="90d">90 jours</option>
          </select>

          <button
            onClick={() => setRange((r) => r)} // re-trigger by state update pattern? (optional)
            className="rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-medium
                       text-slate-900 dark:text-white hover:bg-black/5 dark:hover:bg-white/10 transition"
          >
            Actualiser
          </button>
        </div>
      </div>

      {loading && (
        <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 p-5">
          <div className="h-6 w-48 bg-black/10 dark:bg-white/10 rounded animate-pulse" />
          <div className="mt-4 h-24 bg-black/10 dark:bg-white/10 rounded-2xl animate-pulse" />
        </div>
      )}

      {!loading && err && (
        <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {err}
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard title="Total utilisateurs" value={String(kpis.totalUsers ?? 0)} tone="blue" />
            <StatCard title={`Nouveaux (${range})`} value={String(kpis.newUsersInRange ?? 0)} tone="green" />
            <StatCard title="Commissions (CAD)" value={formatCad(kpis.commissionsCad)} tone="purple" />
            <StatCard
              title="Queue en attente"
              value={`${kpis.pendingQueue?.walletTransactions ?? 0} tx • ${kpis.pendingQueue?.walletTransfers ?? 0} transf`}
              tone="yellow"
            />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Volume by type */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-sm border border-black/5 dark:border-white/10 h-72 flex flex-col">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-3">
                Volume (CAD) par type
              </h3>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="cad" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Wallet daily trend */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-sm border border-black/5 dark:border-white/10 h-72 flex flex-col lg:col-span-2">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-3">
                Tendance journalière (CAD)
              </h3>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={walletSeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="fundCad" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="withdrawCad" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="transferCad" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Quality / outcomes */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-sm border border-black/5 dark:border-white/10">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">Qualité des opérations</h3>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-3">
              <StatCard title="Total ops" value={String(walletTotals.totalOps)} tone="blue" />
              <StatCard title="Confirmées" value={String(walletTotals.completed)} tone="green" />
              <StatCard title="En attente" value={String(walletTotals.pending)} tone="yellow" />
              <StatCard title="Taux de succès" value={`${walletTotals.successRate.toFixed(1)}%`} tone="purple" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
